import React, { useRef, useState, useEffect } from 'react';
import { TextField, Box, CircularProgress, Typography, Alert } from '@mui/material';
import { logger } from '../utils/logger';

const TEL_AVIV_BOUNDS = {
  north: 32.1333,
  south: 32.0504,
  east: 34.8180,
  west: 34.7276
};

// Custom styles for Google Places Autocomplete dropdown
const autocompleteStyles = `
  .pac-container {
    border-radius: 4px;
    margin-top: 5px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    z-index: 1500 !important;
  }
  .pac-item {
    padding: 8px 12px;
    cursor: pointer;
  }
  .pac-item:hover {
    background-color: #f5f5f5;
  }
  .pac-item-selected, .pac-item-selected:hover {
    background-color: #e3f2fd;
  }
`;

const GoogleMapsSearch = ({ onPlaceSelected }) => {
  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const searchInputRef = useRef(null);
  const [searchBox, setSearchBox] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    // Add custom styles for autocomplete dropdown
    const styleTag = document.createElement('style');
    styleTag.innerHTML = autocompleteStyles;
    document.head.appendChild(styleTag);
    
    return () => {
      if (styleTag.parentNode) {
        document.head.removeChild(styleTag);
      }
    };
  }, []);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      // If Google Maps is already loaded, initialize immediately
      if (window.google && window.google.maps) {
        initializeSearchBox();
        return;
      }

      setIsLoading(true);
      setApiError(null);

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapApiKey}&libraries=places&language=en`;
      script.async = true;
      
      script.onload = () => {
        initializeSearchBox();
        setIsLoading(false);
      };
      
      script.onerror = () => {
        setIsLoading(false);
        setApiError('Failed to load Google Maps. Please try again later.');
        logger.error('Google Maps script failed to load');
      };
      
      document.body.appendChild(script);
    };

    const initializeSearchBox = () => {
      if (searchInputRef.current && window.google) {
        try {
          const searchBoxInstance = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            { 
              types: ['establishment'],
              componentRestrictions: { country: 'IL' },
              bounds: new window.google.maps.LatLngBounds(
                { lat: TEL_AVIV_BOUNDS.south, lng: TEL_AVIV_BOUNDS.west },
                { lat: TEL_AVIV_BOUNDS.north, lng: TEL_AVIV_BOUNDS.east }
              ),
              strictBounds: true, // Changed to true to enforce Tel Aviv area
              fields: ['place_id']
            }
          );

          searchBoxInstance.addListener('place_changed', async () => {
            const initialPlace = searchBoxInstance.getPlace();
            if (initialPlace?.place_id) {
              try {
                setIsLoading(true);
                const placeDetails = await getPlaceDetails(initialPlace.place_id);
                logger.log('Place details with photos:', placeDetails);
                onPlaceSelected(placeDetails);
              } catch (error) {
                logger.error('Error processing place:', error);
                setApiError(`Error getting place details: ${error.message}`);
              } finally {
                setIsLoading(false);
              }
            } else {
              setApiError('Please select a valid business from the dropdown');
            }
          });

          setSearchBox(searchBoxInstance);
        } catch (error) {
          logger.error('Error initializing Google Places Autocomplete:', error);
          setApiError('Error initializing search. Please try again.');
        }
      }
    };

    loadGoogleMapsScript();

    return () => {
      if (searchBox) {
        window.google.maps.event.clearInstanceListeners(searchBox);
      }
    };
  }, [mapApiKey, onPlaceSelected]);

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        inputRef={searchInputRef}
        fullWidth
        label="Search for a business"
        placeholder="Type to search businesses in Tel Aviv..."
        variant="outlined"
        size="small"
        error={!!apiError}
        helperText={apiError}
        disabled={isLoading}
      />
      
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading search results...</Typography>
        </Box>
      )}
    </Box>
  );
};

const getPlaceDetails = async (placeId) => {
  if (!window.google) throw new Error('Google Maps not loaded');

  const placesService = new window.google.maps.places.PlacesService(
    document.createElement('div')
  );

  return new Promise((resolve, reject) => {
    placesService.getDetails({
      placeId,
      fields: [
        'name',
        'formatted_address',
        'formatted_phone_number',
        'website',
        'geometry',
        'place_id',
        'photos'
      ]
    }, (place, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        let photoInfo = {};
        if (place.photos?.[0]) {
          const photo = place.photos[0];
          const tempUrl = photo.getUrl({ maxWidth: 1200, maxHeight: 800 });
          photoInfo = {
            photo_reference: photo.photo_reference,
            temp_image_url: tempUrl
          };
        }

        resolve({
          headline: place.name || '',
          address: place.formatted_address || '',
          phone: place.formatted_phone_number || '',
          link: place.website || '',
          lat: place.geometry?.location.lat() || '',
          lon: place.geometry?.location.lng() || '',
          place_id: place.place_id || '',
          ...photoInfo
        });
      } else {
        reject(new Error(`Place Details failed: ${status}`));
      }
    });
  });
};

export default GoogleMapsSearch;